import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useShare } from '../../hooks/useShare'
import { getProjectsByCategory, getVideoUrl, urlFor } from '../../services/sanity'
import type { Project } from '../../types'
import CTAReel from '../CTAReel/CTAReel'
import LikeButton from './LikeButton'
import { VideoProgressBar } from './VideoProgressBar'

interface ReelViewerProps {
  categorySlug?: string      // Optional now
  projects?: Project[]       // NEW - pre-fetched projects
  showCategoryFilter?: boolean  // NEW
}

// Fisher-Yates Shuffle
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

export default function ReelViewer({ categorySlug, projects: propProjects, showCategoryFilter = false }: ReelViewerProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [projects, setProjects] = useState<Project[]>(() => propProjects ? shuffleArray(propProjects) : [])
  const [loading, setLoading] = useState(!propProjects)
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const { share } = useShare()

  useEffect(() => {
    // If projects are provided as prop, use them (shuffled)
    if (propProjects) {
      setProjects(shuffleArray(propProjects))
      setLoading(false)
      return
    }

    // Otherwise fetch by categorySlug
    async function fetchProjects() {
      if (!categorySlug) return

      try {
        const data = await getProjectsByCategory(categorySlug)
        setProjects(shuffleArray(data))
      } catch (error) {
        console.error('Error fetching projects:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchProjects()
  }, [categorySlug, propProjects])

  // Scroll to top when projects change (category switch)
  useEffect(() => {
    if (containerRef.current && projects.length > 0) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
      setActiveIndex(0)
    }
  }, [projects])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'))
            setActiveIndex(index)

            // Auto-play video when in view - restart from beginning (TikTok style)
            const video = videoRefs.current[index]
            if (video) {
              video.currentTime = 0 // Reset to start
              video.play().catch(err => console.log('Auto-play prevented:', err))
            }
          } else {
            const index = Number(entry.target.getAttribute('data-index'))
            // Pause and reset video when out of view
            const video = videoRefs.current[index]
            if (video) {
              video.pause()
              video.currentTime = 0 // Reset to start for next view
            }
          }
        })
      },
      {
        threshold: 0.6,
        root: null,
      }
    )

    const reelItems = containerRef.current?.querySelectorAll('.reel-item')
    reelItems?.forEach((item) => observer.observe(item))

    return () => observer.disconnect()
  }, [projects])

  const togglePlay = (index: number) => {
    const video = videoRefs.current[index]
    if (!video) return

    if (video.paused) {
      video.play()
    } else {
      video.pause()
    }
  }

  const scrollToContact = () => {
    const reelItems = containerRef.current?.querySelectorAll('.reel-item')
    if (reelItems && reelItems.length > 0) {
      // Scroll to last item (CTAReel with contact form)
      const lastItem = reelItems[reelItems.length - 1]
      lastItem.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-white text-2xl">Loading projects...</div>
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="text-4xl mb-4">No Projects Yet</div>
          <div className="text-apple-gray-400">
            Add some projects to this category in Sanity Studio
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="h-screen overflow-y-scroll snap-y snap-mandatory"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        height: '100dvh'
      } as React.CSSProperties}
    >
      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {projects.map((project, index) => (
        <div
          key={project._id}
          data-index={index}
          className={`reel-item h-screen snap-start snap-always relative bg-black flex items-center justify-center ${showCategoryFilter ? 'pt-24 md:pt-32' : ''}`}
          style={{ height: '100dvh' } as React.CSSProperties}
        >
          {project.featuredVideo ? (
            <>
              {/* Phone Wrapper - garantiert gleiche Größe für Video und Bezel */}
              <div className="relative w-[75vw] h-[75vh] max-w-[380px] md:w-[380px] md:h-[75vh] md:max-h-[800px]">
                {/* Video Container */}
                <div className="absolute inset-0 rounded-[40px] md:rounded-[60px] overflow-hidden">
                  <video
                    ref={(el) => { videoRefs.current[index] = el }}
                    className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                    loop
                    muted
                    playsInline
                    poster={project.videoThumbnail ? urlFor(project.videoThumbnail).width(1080).height(1920).url() : undefined}
                    onClick={() => togglePlay(index)}
                  >
                    <source src={getVideoUrl(project.featuredVideo.asset._ref)} type="video/mp4" />
                  </video>

                  {/* Progress Bar */}
                  <VideoProgressBar
                    videoRef={videoRefs.current[index]}
                    isActive={activeIndex === index}
                  />
                </div>

                {/* iPhone Bezel */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Bezel Border */}
                  <div
                    className="absolute inset-0 rounded-[40px] md:rounded-[60px]"
                    style={{
                      boxShadow: '0 0 0 10px black, 0 15px 40px rgba(0,0,0,0.5), 0 0 0 11px rgba(255,255,255,0.1)'
                    }}
                  />

                  {/* Home Indicator */}
                  <div className="absolute bottom-2 md:bottom-4 left-1/2 -translate-x-1/2 w-24 md:w-32 h-1 md:h-1.5 bg-white/20 rounded-full" />
                </div>

                {/* UI Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Top gradient */}
                  <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/50 to-transparent" />

                  {/* Bottom gradient */}
                  <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black/70 to-transparent" />

                  {/* Project info */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 md:bottom-4 md:left-4 md:right-4 pointer-events-auto">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="max-w-full"
                    >
                      <h3 className="text-white text-lg md:text-xl font-semibold mb-1 md:mb-2">
                        {project.title}
                      </h3>
                      <p className="text-white/80 text-xs md:text-sm mb-3 md:mb-4">
                        {project.description || 'Bei Interesse einfach'}
                      </p>
                      <button
                        onClick={scrollToContact}
                        className="inline-block px-4 py-1.5 text-xs md:text-sm bg-white text-black rounded-full font-medium hover:bg-apple-gray-100 transition-smooth"
                      >
                        Kontakt anfragen
                      </button>
                    </motion.div>
                  </div>

                  {/* Right side indicators */}
                  <div className="absolute right-3 bottom-32 md:right-2 md:bottom-40 flex flex-col space-y-4 md:space-y-4 pointer-events-auto">
                    <LikeButton projectId={project._id} />
                    <button
                      onClick={() =>
                        share({
                          title: project.title,
                          text: project.description || 'Check out this VFX project!',
                          url: window.location.href,
                        })
                      }
                      className="w-10 h-10 md:w-10 md:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-smooth"
                    >
                      <svg
                        className="w-5 h-5 md:w-6 md:h-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-apple-gray-400">
              <div className="text-center">
                <div className="text-2xl">{project.title}</div>
                <div className="text-sm mt-2">No video available</div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* CTA Reel - Always last */}
      <div
        data-index={projects.length}
        className="reel-item"
      >
        <CTAReel />
      </div>

      {/* Scroll indicator - hidden when at CTA Reel */}
      <AnimatePresence>
        {activeIndex !== projects.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="fixed top-1/2 right-4 md:right-[calc((100vw-380px)/2-1.5rem)] transform -translate-y-1/2 z-50"
          >
            <div className="flex flex-col items-center space-y-2">
              {/* Project indicators */}
              {projects.map((_, index) => (
                <div
                  key={index}
                  className={`w-1 md:w-1.5 h-1 md:h-1.5 rounded-full transition-all ${activeIndex === index
                    ? 'bg-white h-6 md:h-8'
                    : 'bg-white/40'
                    }`}
                />
              ))}
              {/* CTA Reel indicator */}
              <div
                className={`w-1 md:w-1.5 h-1 md:h-1.5 rounded-full transition-all ${activeIndex === projects.length
                  ? 'bg-white h-6 md:h-8'
                  : 'bg-white/40'
                  }`}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
